/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { type SceneDoc, type SceneOp } from '../scene-doc/schema';
import { applyOps, emptyDoc } from '../scene-doc/reducer';
import { canRedo, canUndo, commit, initialHistory, redo, undo, type History } from '../scene-doc/undo';
import type { DocumentStore } from '../services/document-store';

export interface UseSceneDocResult {
  doc: SceneDoc;
  /** Ids, not nodes: a selection has to survive the edit that replaces the node object. */
  selection: readonly string[];
  select: (ids: readonly string[]) => void;
  /** One batch is one undo entry, whether it came from a button or the agent. */
  dispatch: (ops: readonly SceneOp[]) => void;
  /** A drag, which folds into the entry the grab opened rather than making one per frame. */
  dispatchContinuous: (ops: readonly SceneOp[]) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  errors: readonly string[];
  /** False until the stored document has been read, so nothing is saved over it. */
  loaded: boolean;
  /** When the document was last written, and what stopped it if it was not. */
  savedAt: Date | undefined;
  saveError: Error | undefined;
}

/** Devplan §5.2. Long enough that a drag is one write, short enough that a closed tab loses nothing. */
const AUTOSAVE_MS = 10_000;

interface DocState {
  history: History<SceneDoc>;
  errors: readonly string[];
  /** True between a gizmo grab and its release. */
  coalescing: boolean;
}

type DocAction =
  | { type: 'ops'; ops: readonly SceneOp[]; coalesce: boolean }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'break' }
  | { type: 'loaded'; doc: SceneDoc };

/** Pure, so React may run it twice in development without anything happening twice. */
const reduce = (state: DocState, action: DocAction): DocState => {
  switch (action.type) {
    case 'ops': {
      const { doc, errors } = applyOps(state.history.present, action.ops);
      const merge = action.coalesce && state.coalescing;
      return {
        history: merge ? { ...state.history, present: doc } : commit(state.history, doc),
        errors,
        coalescing: action.coalesce,
      };
    }
    case 'undo':
      return { history: undo(state.history), errors: [], coalescing: false };
    case 'redo':
      return { history: redo(state.history), errors: [], coalescing: false };
    case 'break':
      return state.coalescing ? { ...state, coalescing: false } : state;
    // What was stored replaces the empty document the editor opened on, rather
    // than being committed onto it: there is nothing yet to undo back to.
    case 'loaded':
      return { history: initialHistory(action.doc), errors: [], coalescing: false };
  }
};

export const useSceneDoc = (store: DocumentStore): UseSceneDocResult => {
  const [state, act] = useReducer(reduce, undefined, () => ({
    history: initialHistory(emptyDoc()),
    errors: [],
    coalescing: false,
  }));
  const [selected, setSelected] = useState<readonly string[]>([]);
  // Which store has been read, rather than a flag: opening a different scene
  // makes the answer stale by itself, with nothing to reset.
  const [opened, setOpened] = useState<DocumentStore>();
  const [savedAt, setSavedAt] = useState<Date>();
  const [saveError, setSaveError] = useState<Error>();

  const doc = state.history.present;
  const loaded = opened === store;

  // The document as it was last written, so an edit is told apart from the load
  // that put it there.
  const saved = useRef<SceneDoc>(doc);

  useEffect(() => {
    let current = true;

    store
      .load()
      .then((stored) => {
        if (!current) {
          return;
        }
        if (stored) {
          saved.current = stored;
          act({ type: 'loaded', doc: stored });
        }
        setOpened(store);
      })
      .catch((error: Error) => {
        if (current) {
          // Opening failed, so saving would write an empty document over
          // whatever is there. The page reports it and autosave stays shut.
          setSaveError(error);
        }
      });

    return () => {
      current = false;
    };
  }, [store]);

  // Autosave, per devplan §5.2 — the document only, never a built `.babylon`.
  // The timer is reset by every edit, so a drag is one write rather than ten.
  useEffect(() => {
    if (!loaded || doc === saved.current) {
      return;
    }

    const timer = setTimeout(() => {
      saved.current = doc;
      store.save(doc).then(
        () => {
          setSavedAt(new Date());
          setSaveError(undefined);
        },
        (error: Error) => setSaveError(error)
      );
    }, AUTOSAVE_MS);

    return () => clearTimeout(timer);
  }, [doc, loaded, store]);

  const select = useCallback((ids: readonly string[]) => {
    act({ type: 'break' });
    setSelected(ids);
  }, []);

  // A deleted node must not stay selected, and undo has to bring its selection
  // back — both fall out of deriving this rather than storing the survivors.
  const selection = useMemo(() => selected.filter((id) => doc.nodes.some((node) => node.id === id)), [selected, doc]);

  return useMemo(
    () => ({
      doc,
      selection,
      select,
      dispatch: (ops: readonly SceneOp[]) => act({ type: 'ops', ops, coalesce: false }),
      dispatchContinuous: (ops: readonly SceneOp[]) => act({ type: 'ops', ops, coalesce: true }),
      undo: () => act({ type: 'undo' }),
      redo: () => act({ type: 'redo' }),
      canUndo: canUndo(state.history),
      canRedo: canRedo(state.history),
      errors: state.errors,
      loaded,
      savedAt,
      saveError,
    }),
    [doc, selection, select, state.history, state.errors, loaded, savedAt, saveError]
  );
};
